import { useState, useCallback, useRef, useEffect } from "react";

interface UseAutoSaveOptions {
	debounceMs?: number;
	isDirty: boolean;
	onSave: () => Promise<void | boolean>;
}

interface UseAutoSaveResult {
	isSaving: boolean;
	lastSavedAt: Date | null;
	triggerSave: () => void;
}

export const useAutoSave = ({
	isDirty,
	onSave,
	debounceMs = 2500,
}: UseAutoSaveOptions): UseAutoSaveResult => {
	const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const onSaveRef = useRef(onSave);
	const isMountedRef = useRef(true);

	onSaveRef.current = onSave;

	const clearTimer = useCallback(() => {
		if (timerRef.current !== null) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const executeSave = useCallback(async () => {
		if (!isMountedRef.current) {
			return;
		}
		setIsSaving(true);
		try {
			await onSaveRef.current();
			if (isMountedRef.current) {
				setLastSavedAt(new Date());
			}
		} finally {
			if (isMountedRef.current) {
				setIsSaving(false);
			}
		}
	}, []);

	const triggerSave = useCallback(() => {
		clearTimer();
		timerRef.current = setTimeout(() => {
			executeSave();
		}, debounceMs);
	}, [clearTimer, executeSave, debounceMs]);

	useEffect(() => {
		if (isDirty) {
			triggerSave();
		}
		return clearTimer;
	}, [isDirty, triggerSave, clearTimer]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			clearTimer();
		};
	}, [clearTimer]);

	return {
		lastSavedAt,
		isSaving,
		triggerSave,
	};
};
